// @flow
import i18n from "i18next";
import k from "src/i18n/keys";
import React, { useState, useMemo } from "react";
import { connect } from "react-redux";

import User from "src/components/user/Photo";
import NameOrEmail from "src/containers/user/NameOrEmail";
import SelectIndicator from "src/components/messages/Chat/SelectIndicator";
import MessageCard from "src/components/messages/Chat/MessageCard";
import {
  ChatCard,
  UserInfo,
  MessageHeader as NotificationHeader,
  NotificationNameOrEmail,
} from "src/components/messages/Chat/styles";
import MoreMenu from "src/components/messages/MoreMenu";
import {
  getCurrentUserId,
  getCurrentRoomId,
  getChatroomMembership,
  getSelectedAction,
} from "src/reducers";
import { getNotificationApprovalInfo, getLabel } from "src/utils/checklist";
import ChatTimestamp from "src/components/messages/Chat/ChatTimestamp";

import type {
  AppState,
  UID,
  ChecklistFieldNotificationData,
  UnifizeChat,
} from "src/types";

import { ChecklistNotification, NotificationHeaderTitle } from "../styles";
import { ChecklistFieldLabel } from "./styles";
import ChecklistContent from "./Content";

type Props = {
  message: any,
  currentUser: UID,
  selectedAction: string,
  isMember: boolean,
  groupPosition?: string,
};

const ChecklistFields = ({
  message,
  currentUser,
  selectedAction,
  isMember,
  groupPosition,
}: Props) => {
  const {
    id,
    timestamp,
    author,
    data,
    delivered,
    read,
  }: { ...$Exact<UnifizeChat>, data: ChecklistFieldNotificationData } = message;
  const fieldIds: Array<string> = Object.keys(data.fields);
  const isCurrentUser = author === currentUser;
  const isOwner = currentUser === author;

  const hasApproval = useMemo(
    () =>
      Object.values(data.fields).some(
        (field: any) => getNotificationApprovalInfo(field).approval,
      ),
    [data.fields],
  );
  const hasRequest: ?boolean = useMemo(() => {
    const firstField = data.fields[parseInt(fieldIds[0], 10)];
    if (
      hasApproval &&
      firstField &&
      Object.prototype.hasOwnProperty.call(firstField?.new || {}, "request")
    ) {
      return firstField?.new?.request;
    }
    return null;
  }, [data.fields, hasApproval]);

  const updatedTerm = useMemo(() => {
    const firstField = data.fields[parseInt(fieldIds[0], 10)];
    if (hasRequest != null) {
      return (
        <>
          {hasRequest === true ? i18n.t(k.REQUESTED) : i18n.t(k.CANCELLED)}{" "}
          <ChecklistFieldLabel>
            {getLabel(firstField.type) ?? ""}
          </ChecklistFieldLabel>
        </>
      );
    }
    if (fieldIds.length > 1) {
      return i18n.t(k.UPDATED_CHECKLIST);
    } else {
      return (
        <>
          {i18n.t(k.UPDATED1)}{" "}
          <ChecklistFieldLabel>
            {getLabel(firstField.type) ?? ""}
          </ChecklistFieldLabel>
        </>
      );
    }
  }, [fieldIds, data.fields]);
  const [menu, setMenu] = useState(false);

  const handleMouseEnter = () => {
    setMenu(true);
  };

  const handleMouseLeave = () => {
    setMenu(false);
  };

  return (
    <ChecklistNotification
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SelectIndicator
        id={id}
        timestamp={timestamp}
        isOwner={isOwner}
        disabled={true}
      />
      <ChatCard showSelect={selectedAction} first={groupPosition === "first"}>
        {groupPosition === "first" && (
          <UserInfo>
            <User uid={author} size="sm" />
          </UserInfo>
        )}
        <MessageCard
          first={groupPosition === "first"}
          isChecklistNotification={
            (!hasApproval || fieldIds.length > 1) && hasRequest == null
          }
          width={
            hasApproval && fieldIds.length === 1 && hasRequest === null
              ? "360px"
              : "auto"
          }
        >
          <NotificationHeader>
            <NotificationHeaderTitle>
              {isCurrentUser ? (
                <NotificationNameOrEmail>
                  {i18n.t(k.YOU)}
                </NotificationNameOrEmail>
              ) : (
                <NameOrEmail uid={author} notificationVariant="key" />
              )}
              {updatedTerm}
            </NotificationHeaderTitle>
            <ChatTimestamp
              read={read ?? false}
              delivered={delivered ?? false}
              timestamp={timestamp}
            />
          </NotificationHeader>
          <ChecklistContent
            timestamp={timestamp}
            data={data}
            isApproval={hasApproval}
            isApprovalRequest={hasRequest !== null}
          />
        </MessageCard>
      </ChatCard>
      {menu && isMember ? (
        <MoreMenu id={id} timestamp={timestamp} isOwner={isOwner} />
      ) : null}
    </ChecklistNotification>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  currentUser: getCurrentUserId(app),
  selectedAction: getSelectedAction(app),
  isMember: getChatroomMembership(app, getCurrentRoomId(app)),
});

export default connect(mapStateToProps)(ChecklistFields);
